import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Send, Globe, CheckCircle, XCircle, Clock, Loader2, RefreshCw, Trash2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminIndexing = () => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [singleUrl, setSingleUrl] = useState('');
  const [submittingSingle, setSubmittingSingle] = useState(false);

  const fetchStatus = useCallback(async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.get(`${API_URL}/api/google-indexing/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStatus(response.data);
    } catch (error) {
      console.error('Error fetching indexing status:', error);
      if (error.response?.status === 401) {
        toast.error('Please reconnect your Google account to enable indexing');
      } else {
        toast.error('Failed to fetch indexing status');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  const handleBatchSubmit = async () => {
    setSubmitting(true);
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.post(`${API_URL}/api/google-indexing/batch`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const data = response.data;
      
      if (data.submitted > 0) {
        toast.success(`Successfully submitted ${data.submitted} URLs to Google for indexing!`);
      } else if (data.rate_limited) {
        toast.warning('Google rate limit reached. Try again tomorrow.');
      } else {
        toast.info(data.message || 'No new URLs to submit');
      }
      
      // Refresh status
      await fetchStatus();
      
    } catch (error) {
      console.error('Error batch submitting:', error);
      if (error.response?.status === 401) {
        toast.error('Please reconnect your Google account in GBP Automation to enable indexing');
      } else {
        toast.error(error.response?.data?.detail || 'Failed to submit URLs');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleSingleSubmit = async (e) => {
    e.preventDefault();
    if (!singleUrl.trim()) return;
    
    setSubmittingSingle(true);
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.post(`${API_URL}/api/google-indexing/submit`, 
        { url: singleUrl.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        toast.success(`URL submitted: ${singleUrl}`);
        setSingleUrl('');
      } else {
        toast.error(response.data.error || 'Failed to submit URL');
      }
      
      // Refresh status
      await fetchStatus();
      
    } catch (error) {
      console.error('Error submitting URL:', error);
      toast.error(error.response?.data?.detail || 'Failed to submit URL');
    } finally {
      setSubmittingSingle(false);
    }
  };

  const formatDate = (isoString) => {
    if (!isoString) return 'N/A';
    const date = new Date(isoString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const truncateUrl = (url) => {
    if (!url) return '';
    // Remove domain prefix for cleaner display
    return url.replace('https://fortworthdental.com', '').replace('https://www.fortworthdental.com', '') || '/';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-teal-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-indexing-page">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Google Indexing API</h1>
          <p className="text-gray-500 mt-1">
            Submit URLs directly to Google for faster crawling and indexing
          </p>
        </div>
        <Button
          onClick={fetchStatus}
          variant="outline"
          size="sm"
          data-testid="refresh-status-btn"
        >
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card data-testid="stat-today">
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-sm font-medium text-gray-500">Today</p>
              <p className="text-3xl font-bold text-teal-600">
                {status?.today_submitted || 0}
              </p>
              <p className="text-xs text-gray-400 mt-1">
                {status?.today_remaining || 200} remaining
              </p>
            </div>
          </CardContent>
        </Card>

        <Card data-testid="stat-week">
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-sm font-medium text-gray-500">This Week</p>
              <p className="text-3xl font-bold text-blue-600">
                {status?.week_submitted || 0}
              </p>
              <p className="text-xs text-gray-400 mt-1">URLs indexed</p>
            </div>
          </CardContent>
        </Card>

        <Card data-testid="stat-total">
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-sm font-medium text-gray-500">All Time</p>
              <p className="text-3xl font-bold text-purple-600">
                {status?.total_submitted || 0}
              </p>
              <p className="text-xs text-gray-400 mt-1">Total submitted</p>
            </div>
          </CardContent>
        </Card>

        <Card data-testid="stat-pages">
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-sm font-medium text-gray-500">Site Pages</p>
              <p className="text-3xl font-bold text-orange-600">
                {status?.total_pages || 0}
              </p>
              <p className="text-xs text-gray-400 mt-1">Discoverable pages</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Actions */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Batch Submit */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Globe className="w-5 h-5 text-teal-600" />
              Batch Submit All Pages
            </CardTitle>
            <CardDescription>
              Submit all unindexed pages to Google (up to 200/day). Pages submitted within the last 7 days are skipped.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button
              onClick={handleBatchSubmit}
              disabled={submitting || (status?.today_remaining <= 0)}
              className="w-full bg-teal-600 hover:bg-teal-700"
              data-testid="batch-submit-btn"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Submitting...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Submit Batch ({status?.today_remaining || 200} available)
                </>
              )}
            </Button>
            {status?.today_remaining <= 0 && (
              <p className="text-sm text-red-500 mt-2 text-center">
                Daily limit reached. Try again tomorrow.
              </p>
            )}
          </CardContent>
        </Card>

        {/* Single URL Submit */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Send className="w-5 h-5 text-blue-600" />
              Submit Single URL
            </CardTitle>
            <CardDescription>
              Submit a specific URL for immediate indexing
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSingleSubmit} className="flex gap-2">
              <input
                type="url"
                value={singleUrl}
                onChange={(e) => setSingleUrl(e.target.value)}
                placeholder="https://fortworthdental.com/your-page"
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                required
                data-testid="single-url-input"
              />
              <Button
                type="submit"
                disabled={submittingSingle || !singleUrl.trim()}
                className="bg-blue-600 hover:bg-blue-700"
                data-testid="single-submit-btn"
              >
                {submittingSingle ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Send className="w-4 h-4" />
                )}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>

      {/* Recent Submissions */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="w-5 h-5 text-gray-600" />
            Recent Submissions
          </CardTitle>
          <CardDescription>
            Last 20 URLs submitted to Google Indexing API
          </CardDescription>
        </CardHeader>
        <CardContent>
          {status?.recent && status.recent.length > 0 ? (
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {status.recent.map((item, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between py-2 px-3 bg-gray-50 rounded-lg"
                  data-testid={`submission-item-${index}`}
                >
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    {item.status === 'success' ? (
                      <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                    ) : (
                      <XCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
                    )}
                    <span className="text-sm text-gray-700 truncate" title={item.url}>
                      {truncateUrl(item.url)}
                    </span>
                  </div>
                  <div className="flex items-center gap-3 flex-shrink-0">
                    <span className={`text-xs px-2 py-1 rounded ${
                      item.status === 'success' 
                        ? 'bg-green-100 text-green-700' 
                        : 'bg-red-100 text-red-700'
                    }`}>
                      {item.status_code || 'ERR'}
                    </span>
                    <span className="text-xs text-gray-400 w-24 text-right">
                      {formatDate(item.submitted_at)}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <Globe className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p>No submissions yet. Click "Submit Batch" to get started!</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Info Box */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="pt-6">
          <h3 className="font-semibold text-blue-800 mb-2">How Google Indexing API Works</h3>
          <ul className="text-sm text-blue-700 space-y-1">
            <li>- Google allows up to <strong>200 URL submissions per day</strong></li>
            <li>- Submitted URLs are prioritized for crawling (typically within minutes to hours)</li>
            <li>- URLs submitted in the last 7 days are automatically skipped to avoid duplicates</li>
            <li>- This works alongside your XML sitemap for comprehensive coverage</li>
            <li>- Make sure your Google account has the "indexing" scope enabled in GBP settings</li>
          </ul>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminIndexing;
